/**
 * tracking-lib-directive.ts
 *
 * Fan-out tracking directives for the CPP dual-system migration period.
 *
 * Background:
 *   The legacy nhstDataLayer / Adobe Satellite path and the new @nhst/tracking-lib
 *   path both need to receive every click and viewport-intersection event during
 *   the transition. The naive approach is to register two directives on every
 *   tracked element, but that mounts two IntersectionObservers per scroll marker
 *   and two click listeners per card — which on a 60-card frontpage is real cost.
 *
 *   These directives install ONE listener / ONE observer and emit to BOTH
 *   systems. Templates only carry v-track-event / v-track-scroll. When the
 *   legacy data layer is fully retired:
 *
 *     1. Strip the `emit legacy` branch from `emitClick` / `emitScroll`, or
 *     2. Delete this plugin and pass `vueApp: nuxtApp.vueApp` to
 *        initializeTracking() in tracking-lib.client.ts so the library
 *        registers v-track-event / v-track-scroll officially.
 *
 * Binding shape (matches @nhst/tracking-lib):
 *
 *   v-track-event="{
 *     component:  { zone, element, element_id },
 *     attributes: { ... }
 *   }"
 *   v-track-event="() => ({ ... })"  // getter form for reactive data
 *
 *   v-track-scroll="{
 *     component:    { zone, element, element_id },
 *     attributes:   { ... },
 *     trackOptions: { trackCount, trackDirection, trackStartPercentage }
 *   }"
 */
import type { DirectiveBinding, ObjectDirective, VNode } from 'vue';
import { trackClick, trackScroll } from '@nhst/tracking-lib';
import { useDataLayerEvent } from '~/composables/useDataLayerEvent';
import { toContractEvent, addComponentProps } from '~/utils/payloadShape';

interface ComponentBinding {
  zone?: string;
  element?: string;
  element_id?: string;
}

interface TrackOptions {
  trackDirection?: 'top' | 'bottom';
  trackCount?: number;
  trackStartPercentage?: number;
  // Internal book-keeping — mutated on the binding object across observer ticks.
  trackCounted?: { top?: number; bottom?: number };
  isVisited?: boolean;
}

export interface TrackBinding {
  component?: ComponentBinding;
  attributes?: Record<string, unknown>;
  // Contract-only content fields — read by toContractEvent, never by toLegacyAction.
  content?: Record<string, unknown>;
  eventName?: string;
  /*
   * Scroll-marker slot identifier (new contract only). Top-level so it never enters the
   * legacy bag via toLegacyAction's `...attributes` spread.
   */
  slot?: string;
  trackOptions?: TrackOptions;
}

type BindingValue = TrackBinding | (() => TrackBinding);

export function resolveBinding (value: BindingValue): TrackBinding {
  return typeof value === 'function' ? (value as () => TrackBinding)() : value || {};
}

let userHasScrolled = false;
let pointerDown = false;

function resetScrollGate (): void {
  userHasScrolled = false;
}

function initGestureGate (): void {
  resetScrollGate();
  useEventListener(window, ['wheel', 'touchmove', 'keydown'], () => {
    userHasScrolled = true;
  }, { passive: true });
  useEventListener(window, 'pointerdown', () => { pointerDown = true; }, { passive: true });
  useEventListener(window, ['pointerup', 'pointercancel'], () => { pointerDown = false; }, { passive: true });
  useEventListener(window, 'scroll', () => {
    if (pointerDown) { userHasScrolled = true; }
  }, { passive: true });
  useEventListener(window, 'popstate', resetScrollGate);
}

/**
 * Maps the new-style binding back to the legacy action/content shape used by
 * useDataLayerEvent.constructEventInfo(). Exported for unit testing.
 */
export function toLegacyAction (b: TrackBinding): Record<string, unknown> {
  const { component = {}, attributes = {}, eventName } = b;
  return {
    eventName,
    component: component.zone,
    elementName: component.element,
    elementId: component.element_id,
    teaserType: attributes.teaser_type,
    rowIndex: attributes.row_index,
    rowLayout: attributes.row_layout,
    elementPosition: attributes.element_position,
    linkUrl: attributes.link_url,
    streamId: attributes.stream_id,
    elementText: attributes.element_text,
    /*
     * Spread LAST so legacy-named attribute keys (elementName, linkUrl, …) never
     * override the explicit mappings above. Callers must use the new-shape keys
     * (element_position, link_url, …) in attributes; legacy names are accepted
     * here only via the explicit pulls.
     */
    ...attributes
  };
}

function emitClick (el: HTMLElement, vnode: VNode | null, binding: TrackBinding): void {
  /*
   * Legacy system: window.nhstDataLayer → Adobe Satellite (unchanged). Built first
   * because constructEventInfo DOM-walks to derive element_position / row_index /
   * row_layout / stream_id / teaser_type / link_url that the binding may not pre-compute.
   */
  const legacy = toLegacyAction(binding);
  const eventInfo = useDataLayerEvent().constructEventInfo(el, legacy, vnode);
  /*
   * constructEventInfo returns `{ action: {}, content: {} }` (loose types); cast
   * narrowly to assign the action verb without resorting to `as any`.
   */
  (eventInfo.action as Record<string, unknown>).action = 'click';

  /*
   * New system: @nhst/tracking-lib — contract-shaped payload, enriched with the
   * DOM-derived component.properties (binding-provided values win).
   */
  const contract = toContractEvent(binding, 'click');
  addComponentProps(contract, eventInfo.action as Record<string, unknown>);
  trackClick(contract);

  const fullName = ['Click', binding.eventName].filter(Boolean).join(' | ');
  useDataLayerEvent().userEvent(fullName, eventInfo);
}

function emitScroll (el: HTMLElement, vnode: VNode | null, binding: TrackBinding, _direction: 'top' | 'bottom'): void {
  /*
   * Legacy system (unchanged) — built first so its DOM-walked fields can enrich the
   * new-lib component.properties. scroll direction is not part of the producer contract;
   * the legacy stream still carries it via constructEventInfo.
   */
  const legacy = toLegacyAction(binding);
  const eventInfo = useDataLayerEvent().constructEventInfo(el, legacy, vnode);
  (eventInfo.action as Record<string, unknown>).action = 'scroll';

  // New system — contract-shaped payload, enriched with DOM-derived component.properties.
  const contract = toContractEvent(binding, 'scroll');
  addComponentProps(contract, eventInfo.action as Record<string, unknown>);
  trackScroll(contract);

  const fullName = ['Scroll', binding.eventName].filter(Boolean).join(' | ');
  useDataLayerEvent().userEvent(fullName, eventInfo);
}

/*
 * ---------------------------------------------------------------------------
 * Click directive
 * ---------------------------------------------------------------------------
 */

export const fanOutClick: ObjectDirective<HTMLElement, BindingValue> = {
  mounted (el, binding: DirectiveBinding<BindingValue>, vnode) {
    // useEventListener auto-cleans on unmount and matches the legacy lifecycle.
    useEventListener(el, 'click', () => {
      emitClick(el, vnode, resolveBinding(binding.value));
    });
  }
};

/*
 * ---------------------------------------------------------------------------
 * Scroll directive (single IntersectionObserver, fans out to both systems)
 * ---------------------------------------------------------------------------
 */

export function updateTrackCount (opts: TrackOptions, direction: 'top' | 'bottom'): void {
  opts.trackCounted = opts.trackCounted || {};
  opts.trackCounted[direction] = (opts.trackCounted[direction] || 0) + 1;
}

export function shouldTrack (opts: TrackOptions, direction: 'top' | 'bottom'): boolean {
  if (!opts.trackCount) { return true; }
  const seen = opts.trackCounted?.[direction] || 0;
  return seen < opts.trackCount;
}

export const fanOutScroll: ObjectDirective<HTMLElement, BindingValue> = {
  mounted (el, binding: DirectiveBinding<BindingValue>, vnode) {
    let yPos = 0;
    const initial = resolveBinding(binding.value);
    initial.trackOptions = initial.trackOptions || {};
    initial.trackOptions.isVisited = false;
    initial.trackOptions.trackStartPercentage = initial.trackOptions.trackStartPercentage || 0;

    /*
     * Getter form re-resolves on every tick so reactive values are fresh,
     * but trackCounted state still lives on the initial trackOptions object
     * for both forms (mirrors legacy semantics where binding.value is reused).
     */
    const isFn = typeof binding.value === 'function';

    useIntersectionObserver(
      el,
      ([{ isIntersecting }]) => {
        const current = isFn ? resolveBinding(binding.value) : initial;
        const opts: TrackOptions = (current.trackOptions = current.trackOptions || initial.trackOptions || {});

        if (isIntersecting) { opts.isVisited = true; }
        if (!isIntersecting && !opts.isVisited) { return; }
        if (!userHasScrolled) { return; }

        const y = window.scrollY;
        const scrollDirection = y > yPos ? 'down' : 'up';
        yPos = y;

        let direction: 'top' | 'bottom';
        if ((isIntersecting && scrollDirection === 'down') || (!isIntersecting && scrollDirection === 'up')) {
          direction = 'top';
        } else if ((isIntersecting && scrollDirection === 'up') || (!isIntersecting && scrollDirection === 'down')) {
          direction = 'bottom';
        } else {
          return;
        }

        if (opts.trackDirection && opts.trackDirection !== direction) { return; }
        if (!shouldTrack(opts, direction)) { return; }
        updateTrackCount(opts, direction);

        emitScroll(el, vnode, current, direction);
      },
      { threshold: initial.trackOptions.trackStartPercentage }
    );
  }
};

/*
 * ---------------------------------------------------------------------------
 * Plugin registration
 * ---------------------------------------------------------------------------
 */

export default defineNuxtPlugin((nuxtApp) => {
  if (import.meta.client) {
    initGestureGate();
    nuxtApp.$router.afterEach(() => resetScrollGate());
  }

  /*
   * Single fan-out directive, registered under the canonical names.
   * Templates carry v-track-event / v-track-scroll only — no more `-next`
   * aliases, no more dual directives on the same element.
   */
  nuxtApp.vueApp.directive('trackEvent', fanOutClick);
  nuxtApp.vueApp.directive('trackScroll', fanOutScroll);
});
